import { LazyLoadResult, Ng2StateDeclaration, UIRouter, UIView } from '@uirouter/angular';
import { SlidesStates } from './slides-states';
import { AuthService } from '@auth0/auth0-angular';
import { AuthStateNames, AuthStatesHelper } from 'src/app/authentication/router/auth-states';
import { first, lastValueFrom } from 'rxjs';
import { handle } from 'src/shared-utilities/async-helpers/promise-helpers';
import { defaultStateRedirectHandler } from './slides-redirect-handlers';
import { ClientLanguageService } from 'src/shared-utilities/services/client-language.service';
import { isStringNullOrEmpty } from 'src/shared-utilities/string-helpers';
import { APP_CONFIG, AppConfig } from 'src/environments/app-environment.models';

export function addQueryParamsToUrl(url: string, queryParams: Record<string, unknown>): string {
  if (isStringNullOrEmpty(url, true)) {
    return url;
  }

  const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:\/\//i.test(url);
  const parsedReturnUrl = new URL(url, document.location.origin);
  const loginControlParams = new Set(['returnUrl', 'isSignUp']);

  for (const [key, value] of Object.entries(queryParams ?? {})) {
    const isEmptyValue = value === null || value === undefined || value === '';
    if (loginControlParams.has(key) || isEmptyValue || parsedReturnUrl.searchParams.has(key)) {
      continue;
    }

    parsedReturnUrl.searchParams.set(key, String(value));
  }

  return isAbsoluteUrl
    ? parsedReturnUrl.toString()
    : `${parsedReturnUrl.pathname}${parsedReturnUrl.search}${parsedReturnUrl.hash}`;
}

export class ClientJourneyStateNames {
  static readonly Default = 'client.journey';
  static readonly ContextLoader = `${this.Default}.contextLoader`;
  static readonly Home = `${this.Default}.home`;
  static readonly Login = `${this.Default}.login`;
  static readonly Logout = `${this.Default}.logout`;
  static readonly LoggedOutError = `${this.Default}.loggedOutError`;
  static readonly Error = `${this.Default}.error`;
  static readonly Success = `${this.Default}.success`;
  static readonly Dashboard = `${this.Default}.dashboard`;
}

const defaultState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Default,
  url: '/journey?{shortCode}{loanCode}{dealKey}{ceContactKey}{source}',
  data: { documentTitle: 'My Mortgage - Application' },
  params: {
    shortCode: { value: '', dynamic: false },
    loanCode: { dynamic: false },
    dealKey: { dynamic: false },
    ceContactKey: { value: '', squash: true },
    source: { value: '', dynamic: true, squash: true },
  },
  loadComponent: () => import('../journey.component').then(m => m.ClientJourneyComponent),
  onEnter: defaultStateRedirectHandler,
  redirectTo: ClientJourneyStateNames.Home,
};

const homeState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Home,
  url: '/home',
  loadComponent: () => import('../home/home.component').then(m => m.HomeComponent),
  onEnter: async trans => {
    const injector = trans.injector();
    const auth0Service = injector.get<AuthService>(AuthService);
    const [isAuthenticated, _] = await handle(lastValueFrom(auth0Service.isAuthenticated$.pipe(first())));
    if (!isAuthenticated) return true;

    const dashboardTargetState = JourneyStates.getDashboardTargetState(trans.router, false, trans.params('to'));
    return dashboardTargetState;
  },
};

const loginState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Login,
  url: '/login',
  component: UIView,
  params: {
    returnUrl: { value: null, squash: true },
    isSignUp: {
      type: 'bool',
      value: false,
    },
  },
  onEnter: async function (trans) {
    try {
      this.auth0Service.logout({ localOnly: true });
    } catch {}

    const targetParams = trans.params('to');
    let returnUrl = targetParams?.returnUrl;

    if (isStringNullOrEmpty(returnUrl, true)) {
      returnUrl = document.location.href;
    }

    returnUrl = addQueryParamsToUrl(returnUrl, targetParams);

    let loginDoneState = trans.router.stateService.get(AuthStateNames.LoginDone);
    if (loginDoneState.name.includes('**')) {
      await trans.router.stateService.lazyLoad('auth.**');
      loginDoneState = trans.router.stateService.get(AuthStateNames.LoginDone);
    }

    const loginDoneUrl = trans.router.stateService.href(
      loginDoneState,
      { returnUrl },
      { absolute: true, inherit: false },
    );

    const injector = trans.injector();
    const auth0Service = injector.get<AuthService>(AuthService);
    const appConfig = injector.get<AppConfig>(APP_CONFIG);
    const clientLanguageService = injector.get<ClientLanguageService>(ClientLanguageService);

    const isSignUp = targetParams?.isSignUp ?? false;

    // Push current url to history so that the back button works from within Auth0's login
    history.pushState({}, '');

    //saves redirect url to local storage before login
    AuthStatesHelper.saveAuthRedirectUrl(returnUrl, appConfig.auth0.redirectUriExpiryTimeSpan);

    auth0Service.loginWithRedirect({
      // Auth0 Angular navigates to appState.target after processing the callback.
      // Keep it on loginDone so the encoded returnUrl (including source) is not replaced with '/'.
      appState: { target: loginDoneUrl },
      authorizationParams: {
        redirect_uri: loginDoneUrl,
        redirectMethod: 'replace',
        audience: appConfig.auth0.authorizationParams.audience,
        ui_locales: clientLanguageService.auth0LanguageIdentifier,
        screen_hint: isSignUp ? 'signup' : '',
      },
    });
  },
};

const logoutState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Logout,
  url: '/logout',
  component: UIView,
  onEnter: async function (trans) {
    // The redirectUrl is where loginDone should redirect to
    const globalParams = trans.router.globals.params;
    const redirectParams = { shortCode: globalParams.shortCode };

    let homeState = trans.router.stateService.get(ClientJourneyStateNames.Home);
    if (homeState.name.includes('**')) {
      await trans.router.stateService.lazyLoad('client.**');
      homeState = trans.router.stateService.get(ClientJourneyStateNames.Home);
    }

    const returnUrl = trans.router.stateService.href(homeState, redirectParams, {
      absolute: true,
      inherit: false,
    });

    let logoutDoneState = trans.router.stateService.get(AuthStateNames.LogoutDone);
    if (logoutDoneState.name.includes('**')) {
      await trans.router.stateService.lazyLoad('auth.**');
      logoutDoneState = trans.router.stateService.get(AuthStateNames.LogoutDone);
    }

    const logoutDoneUrl = trans.router.stateService.href(logoutDoneState, { returnUrl }, { absolute: true });

    console.log('Redirecting to logout, with return as:', logoutDoneUrl);
    const auth0Service = trans.injector().get<AuthService>(AuthService);
    auth0Service.logout({
      logoutParams: {
        returnTo: logoutDoneUrl,
      },
    });
  },
};

const loggedOutErrorState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.LoggedOutError,
  url: '/loggedOut',
  loadComponent: () =>
    import('../error/logged-out-error/logged-out-error.component').then(m => m.LoggedOutErrorComponent),
};

const errorState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Error,
  url: '/error',
  loadComponent: () => import('../error/error.component').then(m => m.ErrorComponent),
};

const loadAgentState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.ContextLoader,
  url: '',
  loadComponent: () => import('../layout/context-loader.component').then(m => m.ContextLoaderComponent),
  params: {
    returnUrl: { value: '' },
  },
};

const successState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Success,
  url: '/success',
  loadComponent: () =>
    import('../application-success/application-success.component').then(m => m.ApplicationSuccessComponent),
  onEnter: async trans => {
    const injector = trans.injector();
    const auth0Service = injector.get<AuthService>(AuthService);
    const [isAuthenticated, _] = await handle(lastValueFrom(auth0Service.isAuthenticated$.pipe(first())));
    if (!isAuthenticated) return true;

    trans.abort();
    JourneyStates.transitionToDashboard(trans.router);
  },
};

const dashboardState: Ng2StateDeclaration = {
  name: ClientJourneyStateNames.Dashboard,
  url: '/dashboard?{fromSubmit}',
  loadComponent: () => import('../dashboard/dashboard.component').then(m => m.DashboardComponent),
  data: { documentTitle: 'My Mortgage - Dashboard' },
  params: {
    fromSubmit: {
      value: false,
      type: 'bool',
      squash: true,
      dynamic: true,
    },
  },
  onEnter: async trans => {
    const injector = trans.injector();
    const auth0Service = injector.get<AuthService>(AuthService);
    const [isAuthenticated, _] = await handle(lastValueFrom(auth0Service.isAuthenticated$.pipe(first())));
    if (isAuthenticated) return true;

    trans.abort();
    const params = trans.router.globals.params;
    trans.router.stateService.go(ClientJourneyStateNames.Home, {
      shortCode: params.shortCode,
      dealKey: '',
      loanCode: '',
    });
  },
};

console.log('SlidesStates at runtime:', SlidesStates);
const clientJourneyStates: Array<Ng2StateDeclaration> = [
  defaultState,
  homeState,
  loginState,
  logoutState,
  loggedOutErrorState,
  errorState,
  loadAgentState,
  dashboardState,
  successState,
  ...SlidesStates,
];

export const ClientJourneyStatesLazyLoaded: LazyLoadResult = {
  states: clientJourneyStates,
};

export class JourneyStates {
  static transitionToErrorState(uiRouter: UIRouter): void {
    uiRouter.stateService.go(ClientJourneyStateNames.Error, uiRouter.globals.params, { location: false });
  }

  static transitionToLogin(uiRouter: UIRouter, isSignUp: boolean, returnUrl = '') {
    const targetParams = uiRouter.globals.params;
    const resolvedReturnUrl = addQueryParamsToUrl(returnUrl || document.location.href, targetParams);

    uiRouter.stateService.go(
      ClientJourneyStateNames.Login,
      { ...targetParams, isSignUp: isSignUp, returnUrl: resolvedReturnUrl },
      {
        location: false,
        inherit: false,
      },
    );
  }

  static transitionToLogout(uiRouter: UIRouter) {
    uiRouter.stateService.go(ClientJourneyStateNames.Logout, null, {
      location: false,
      inherit: false,
    });
  }

  static transitionToLoggedOutError(uiRouter: UIRouter) {
    uiRouter.stateService.go(ClientJourneyStateNames.LoggedOutError, null, {
      location: false,
    });
  }

  static getDashboardTargetState(
    uiRouter: UIRouter,
    fromSubmit = false,
    routeParams?: Record<string, unknown>,
  ) {
    const sourceParams = routeParams ?? (uiRouter.globals.params as unknown as Record<string, unknown>);
    const dashboardParams = {
      shortCode: sourceParams.shortCode,
      ceContactKey: sourceParams.ceContactKey,
      source: sourceParams.source,
      fromSubmit,
    };
    const dashboardTarget = uiRouter.stateService.target(ClientJourneyStateNames.Dashboard, dashboardParams, {
      inherit: false,
    });
    return dashboardTarget;
  }

  static transitionToDashboard(uiRouter: UIRouter, fromSubmit = false) {
    const dashboardTargetState = this.getDashboardTargetState(uiRouter, fromSubmit);
    uiRouter.stateService.go(
      dashboardTargetState.state(),
      dashboardTargetState.params(),
      dashboardTargetState.options(),
    );
  }
}
